import { UserType } from 'src/utils/proptypes';
import { BaseResponseType } from './base.type';
import { OrganizationType, ProgramType } from 'src/types/organization.type';
import { PartnerOrderType } from 'src/types/ratehawk.type';
import { Metadata } from './transaction.type';

export type PromotionDetail = {
   promotionId: number;
   codeEligible: boolean;
   description: string;
   typeDescription?: string;
   savingAmount: number;
   promotionTypeId?: number;
};

type HotelData = {
   id: string;
   hid: number;
   address: string;
   latitude?: number;
   longitude?: number;
   name: string;
   thumbnail: string;
   checkin: string;
   checkout: string;
   deleted: boolean;
   email: string;
   front_desk_time_end: string;
   front_desk_time_start: string;
   hotel_chain: string;
   is_closed: boolean;
   is_gender_specification_required: boolean;
   kind: string;
   metapolicy_extra_info: string;
   payment_methods: string[];
   phone: string;
   postal_code: any;
   serp_filters: string[];
   star_certificate: any;
   star_rating: number;
   region: {
      name: string;
      country_code: string;
      id: number;
      iata: string;
      type: string;
      type_v2: string;
   };
};

export enum BookingStatusType {
   CANCELLED = 'cancelled',
   COMPLETED = 'completed',
   CONFIRMED = 'confirmed',
   PREPROCESSING = 'preprocessing',
   PROCESSING = 'processing',
   UNCONFIRMED = 'unconfirmed',
   VOIDED = 'voided',
}

// Display-only vocabulary from the API (NPT-1687). Kept separate from BookingStatusType, which is
// also the source of the Booking Failure Tracker filter options.
export enum BookingDisplayStatusType {
   CANCELLED = 'cancelled',
   COMPLETED = 'completed',
   PREPROCESSING = 'preprocessing',
   PROCESSING = 'processing',
   VOIDED = 'voided',
   WAITING_TO_TRAVEL = 'waiting_to_travel',
}

export enum PaymentStatusType {
   AUTHORIZED = 'authorized',
   CAPTURED = 'captured',
   CAPTURE_PENDING = 'capture_pending',
   SETTLED = 'settled',
   VOIDED = 'voided',
   // NPT-1450 / NPT-1492
   INVOICE_PENDING = 'invoice_pending',
   INVOICE_PAID = 'invoice_paid', // NPT-1492 gap, folded into NPT-1450
   MANUAL_REFUND_PENDING = 'manual_refund_pending',
   // NPT-1888 - supplier already cancelled, refund runs once the original charge settles.
   REFUND_PENDING = 'refund_pending',
}

// NPT-2145 — snapshot of the booked room; null/absent on bookings made before it shipped.
export type BookingRoomDetails = {
   version: number;
   bedding: { label: string | null; count: number | null };
   benefits: string[];
   amenities: string[];
   capturedAt: string;
};

export type BookingType = BaseResponseType & {
   orderId: string;
   agreementNumber: string;
   invoiceId: string;
   adultNumber: number;
   checkinDate: string;
   checkoutDate: string;
   childNumber: number;
   confirmationCode?: string;
   created: string;
   currency: string;
   deleted: any;
   emailOriginal: string;
   // NPT-1636 — the traveller's own contact, separate from the booker's (absent on admin bookings).
   travelerEmail?: string | null;
   travelerPhone?: string | null;
   firstNameOriginal: string;
   freeCancellationBefore: string;
   hotel: HotelData;
   hotelId: string;
   lastNameOriginal: string;
   modified: string;
   organization: OrganizationType;
   program: ProgramType;
   organizationId: string;
   paidAmount: string;
   paidDate: string;
   partnerOrder?: PartnerOrderType;
   partnerOrderId: string;
   roomName: string;
   roomNumber: number;
   roomDetails?: BookingRoomDetails | null;
   status: string;
   displayStatus?: string;
   loyaltyNumber?: string;
   user?: Partial<UserType>;
   userId: string;
   siteId?: string | null;
   site?: { id: string; name?: string } | null;
   // NPT-1988: false for an invoice booking (no card to refund), which only an admin cancels. Status rules still apply on top.
   canCancel?: boolean;
   paymentStatus?: string;
   isCancellable?: boolean;
   notes?: string[];
   cancelled?: string;
   cardId?: string;
   cardInfo?: {
      last4: string;
      expMonth: number;
      expYear: number;
   };
   paymentMethod?: string;
   transaction: { paymentMethod: string; id: string; created: string }[];
   metadata?: Metadata;
   cancellationReason?: string;
   cancellationReasonOther?: string;
   isInvoiceBooking?: boolean;
   manualRefundRequired?: boolean;
   createdByAdminId?: string | null;
   // Admin booking created with "Skip": held under the admin until assign-user (GET /booking/:partnerOrderId).
   isUnassigned?: boolean;
};

export type BookingResponse = {
   data: [];
   total: number;
};
